import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Paper,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Divider,
} from "@mui/material";
import {
  AccountCircle,
  CheckCircle,
  ChevronRight,
  PersonOutline,
  ImageOutlined,
  SchoolOutlined,
  CalendarMonthOutlined,
  CakeOutlined,
  BusinessOutlined,
  EmojiEventsOutlined,
  PaletteOutlined,
  CardMembershipOutlined,
  CancelOutlined,
  CreditCardOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useNavigate, useOutletContext } from "react-router-dom";
import api from "../../api";
import { PREDEFINED_AVATARS, getPredefinedAvatarImageUrl } from "../../utils/studentAvatarOptions";

const prefix = process.env.REACT_APP_STUDENT_PREFIX || "student";
const EXAM_BOARD_STORAGE_KEY = "studentExamBoardId";
const PARENT_PREFIX = process.env.REACT_APP_PARENT_PREFIX || "parent";

const THEME_OPTIONS = [
  { key: "indigo", label: "Indigo", color: "#4F46E5" },
  { key: "sky", label: "Sky", color: "#0EA5E9" },
  { key: "rose", label: "Rose", color: "#F43F5E" },
  { key: "amber", label: "Amber", color: "#F59E0B" },
  { key: "violet", label: "Violet", color: "#8B5CF6" },
];

const formatDobDisplay = (ymd) => {
  if (!ymd) return "Not set";
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const ProfileRow = ({ icon, label, value, onClick, valueLines = 1 }) => (
  <ListItemButton onClick={onClick} sx={{ py: 1.25, px: 1.5, borderRadius: 1 }}>
    <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>{icon}</ListItemIcon>
    <ListItemText
      primary={label}
      secondary={value || undefined}
      secondaryTypographyProps={{ noWrap: valueLines === 1, sx: { maxWidth: 220 } }}
    />
    <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
  </ListItemButton>
);

const StudentProfile = () => {
  const navigate = useNavigate();
  const { openParentSwitch } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [petName, setPetName] = useState("");
  const [selectedAvatarKey, setSelectedAvatarKey] = useState(null);
  const [themeKey, setThemeKey] = useState("indigo");
  const [examBoards, setExamBoards] = useState([]);
  const [examBoardId, setExamBoardId] = useState(() => {
    const raw = localStorage.getItem(EXAM_BOARD_STORAGE_KEY);
    if (raw === null || raw === "" || raw === "all") return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  });
  const [schoolYears, setSchoolYears] = useState([]);
  const [schools, setSchools] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [examBoardOpen, setExamBoardOpen] = useState(false);
  const [schoolYearOpen, setSchoolYearOpen] = useState(false);
  const [dobOpen, setDobOpen] = useState(false);
  const [targetOpen, setTargetOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);

  const [draftSchoolYearId, setDraftSchoolYearId] = useState(null);
  const [draftDob, setDraftDob] = useState("");
  const [draftTargetIds, setDraftTargetIds] = useState([]);

  const requestParentSettings = (openMode) => {
    const path = `/${PARENT_PREFIX}/settings?open=${openMode}`;
    if (typeof openParentSwitch === "function") {
      openParentSwitch(path);
      return;
    }
    toast.info("Use the Parent toggle in the header, then open Settings to manage your plan.");
  };

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, boardsRes, yearsRes, schoolsRes] = await Promise.all([
        api.get(`${prefix}/profile`),
        api.get(`${prefix}/profile/exam-boards`).catch(() => ({ data: { data: [] } })),
        api.get("common/data?param=SchoolYears").catch(() => ({ data: { data: [] } })),
        api.get("common/data?param=TargetSchools").catch(() => ({ data: { data: [] } })),
      ]);

      if (profileRes.data?.success && profileRes.data?.data) {
        const d = profileRes.data.data;
        setProfile(d);
        setPetName(d.pet_name || "");
        setThemeKey(d.theme_key || "indigo");
        if (d.profile_image && String(d.profile_image).startsWith("avatar_")) {
          setSelectedAvatarKey(d.profile_image);
        } else {
          setSelectedAvatarKey(null);
        }
        setDraftSchoolYearId(d.school_year_id || null);
        setDraftDob(d.student_date_of_birth || "");
        setDraftTargetIds(Array.isArray(d.target_school_ids) ? d.target_school_ids : []);
      }

      setExamBoards(Array.isArray(boardsRes.data?.data) ? boardsRes.data.data : []);
      setSchoolYears(Array.isArray(yearsRes.data?.data) ? yearsRes.data.data : []);
      setSchools(Array.isArray(schoolsRes.data?.data) ? schoolsRes.data.data : []);
    } catch (err) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const avatarLabel = useMemo(() => {
    if (!selectedAvatarKey) return "Select";
    const av = PREDEFINED_AVATARS.find((a) => a.key === selectedAvatarKey);
    return av ? av.key.replace("avatar_", "Avatar ") : "Custom";
  }, [selectedAvatarKey]);

  const examBoardLabel = useMemo(() => {
    if (examBoardId == null) return "All";
    const board = examBoards.find((b) => Number(b.id) === Number(examBoardId));
    return board?.code || board?.name || "All";
  }, [examBoardId, examBoards]);

  const themeLabel = THEME_OPTIONS.find((t) => t.key === themeKey)?.label || "Indigo";
  const themeColor = THEME_OPTIONS.find((t) => t.key === themeKey)?.color || "#4F46E5";

  const currentDisplayUrl =
    profile?.profile_image_url ||
    (selectedAvatarKey ? getPredefinedAvatarImageUrl(selectedAvatarKey) : null);

  const saveProfile = async (payload, { multipart = false } = {}) => {
    setSaving(true);
    try {
      const res = multipart
        ? await api.post(`${prefix}/profile`, payload, {
            headers: { "Content-Type": "multipart/form-data" },
          })
        : await api.post(`${prefix}/profile`, payload);

      if (res.data?.success) {
        const d = res.data.data || {};
        const stored = JSON.parse(localStorage.getItem("userData") || "{}");
        const updated = {
          ...stored,
          pet_name: d.pet_name ?? stored.pet_name,
          profile_image: d.profile_image ?? stored.profile_image,
          profile_image_url: d.profile_image_url ?? stored.profile_image_url,
          theme_key: d.theme_key ?? stored.theme_key,
        };
        localStorage.setItem("userData", JSON.stringify(updated));
        await loadProfile();
        toast.success(res.data.message || "Profile saved");
        return true;
      }
      toast.error(res.data?.message || "Failed to save profile");
      return false;
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save profile");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIdentity = async () => {
    const formData = new FormData();
    formData.append("pet_name", petName.trim());
    if (selectedAvatarKey) formData.append("avatar_key", selectedAvatarKey);
    const ok = await saveProfile(formData, { multipart: true });
    if (ok) setEditOpen(false);
  };

  const handleSelectAvatar = async (key) => {
    setSelectedAvatarKey(key);
    const formData = new FormData();
    formData.append("avatar_key", key);
    formData.append("pet_name", petName.trim());
    const ok = await saveProfile(formData, { multipart: true });
    if (ok) setAvatarOpen(false);
  };

  const handleSaveSchoolYear = async () => {
    const ok = await saveProfile({ school_year_id: draftSchoolYearId });
    if (ok) setSchoolYearOpen(false);
  };

  const handleSaveDob = async () => {
    if (!draftDob) {
      toast.error("Date of birth is required");
      return;
    }
    const ok = await saveProfile({ student_date_of_birth: draftDob });
    if (ok) setDobOpen(false);
  };

  const handleSaveTargets = async () => {
    if (!draftTargetIds.length) {
      toast.error("Select at least one target school");
      return;
    }
    const ok = await saveProfile({ target_school_ids: draftTargetIds });
    if (ok) setTargetOpen(false);
  };

  const handleSaveTheme = async (key) => {
    setThemeKey(key);
    const ok = await saveProfile({ theme_key: key });
    if (ok) setThemeOpen(false);
  };

  const handleExamBoardSelect = (id) => {
    setExamBoardId(id);
    if (id == null) localStorage.setItem(EXAM_BOARD_STORAGE_KEY, "all");
    else localStorage.setItem(EXAM_BOARD_STORAGE_KEY, String(id));
    setExamBoardOpen(false);
  };

  const toggleTarget = (id) => {
    setDraftTargetIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", py: 3, px: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: "#1e293b" }}>
        View & edit your profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your account details for the student portal. Subscription, settings, and account deletion are
        managed by a parent.
      </Typography>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Avatar src={currentDisplayUrl} sx={{ width: 64, height: 64, bgcolor: "grey.400" }}>
            {!currentDisplayUrl && <AccountCircle sx={{ fontSize: 40 }} />}
          </Avatar>
          <Box>
            <Typography fontWeight={700}>{profile?.full_name || "Student"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {profile?.pet_name ? `Nick name: ${profile.pet_name}` : "Nick name not set"}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 3, mb: 2, overflow: "hidden" }}>
        <Typography
          variant="overline"
          sx={{ px: 2, pt: 1.5, display: "block", color: "text.secondary", fontWeight: 700 }}
        >
          Account
        </Typography>
        <List disablePadding>
          <ProfileRow
            icon={<PersonOutline />}
            label="Edit Profile"
            value={profile?.full_name || ""}
            onClick={() => setEditOpen(true)}
          />
          <Divider component="li" />
          <ProfileRow
            icon={<ImageOutlined />}
            label="Avatar"
            value={avatarLabel}
            onClick={() => setAvatarOpen(true)}
          />
          <Divider component="li" />
          <ProfileRow
            icon={<SchoolOutlined />}
            label="Exam Board"
            value={examBoardLabel}
            onClick={() => setExamBoardOpen(true)}
          />
          <Divider component="li" />
          <ProfileRow
            icon={<CalendarMonthOutlined />}
            label="School Year"
            value={profile?.school_year || "Not set"}
            onClick={() => {
              setDraftSchoolYearId(profile?.school_year_id || null);
              setSchoolYearOpen(true);
            }}
          />
          <Divider component="li" />
          <ProfileRow
            icon={<CakeOutlined />}
            label="Date of Birth"
            value={formatDobDisplay(profile?.student_date_of_birth)}
            onClick={() => {
              setDraftDob(profile?.student_date_of_birth || "");
              setDobOpen(true);
            }}
          />
          <Divider component="li" />
          <ProfileRow
            icon={<BusinessOutlined />}
            label="Target Schools"
            value={profile?.targeted_school || "Not set"}
            valueLines={2}
            onClick={() => {
              setDraftTargetIds(Array.isArray(profile?.target_school_ids) ? profile.target_school_ids : []);
              setTargetOpen(true);
            }}
          />
          <Divider component="li" />
          <ProfileRow
            icon={<EmojiEventsOutlined />}
            label="Leaderboard"
            value=""
            onClick={() => navigate(`/${prefix}/games/leaderboard`)}
          />
        </List>
      </Paper>

      <Paper sx={{ borderRadius: 3, mb: 2, overflow: "hidden" }}>
        <Typography
          variant="overline"
          sx={{ px: 2, pt: 1.5, display: "block", color: "text.secondary", fontWeight: 700 }}
        >
          Parent management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 0.5 }}>
          Enter the parent PIN to open these options in the parent portal.
        </Typography>
        <List disablePadding>
          <ProfileRow
            icon={<CardMembershipOutlined />}
            label="Manage Plan"
            value="Change or renew subscription"
            onClick={() => requestParentSettings("manage")}
          />
          <Divider component="li" />
          <ProfileRow
            icon={<CancelOutlined />}
            label="Cancel subscription"
            value="Turn off auto-renew"
            onClick={() => requestParentSettings("cancel")}
          />
          <Divider component="li" />
          <ProfileRow
            icon={<CreditCardOutlined />}
            label="Payment options"
            value="Update card or invoices"
            onClick={() => requestParentSettings("payment")}
          />
        </List>
      </Paper>

      <Paper sx={{ borderRadius: 3, mb: 2, overflow: "hidden" }}>
        <Typography
          variant="overline"
          sx={{ px: 2, pt: 1.5, display: "block", color: "text.secondary", fontWeight: 700 }}
        >
          Appearance
        </Typography>
        <List disablePadding>
          <ListItemButton onClick={() => setThemeOpen(true)} sx={{ py: 1.25, px: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: themeColor,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            </ListItemIcon>
            <ListItemText primary="Theme Color" secondary={themeLabel} />
            <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
          </ListItemButton>
        </List>
      </Paper>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your nick name is shown on the leaderboard instead of your real name.
          </Typography>
          <TextField
            fullWidth
            label="Nick name"
            placeholder="e.g. SuperStar, MathWiz"
            value={petName}
            onChange={(e) => setPetName(e.target.value)}
            inputProps={{ maxLength: 100 }}
            helperText={`${petName.length}/100 characters`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={handleSaveIdentity}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={avatarOpen} onClose={() => setAvatarOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Pick an avatar</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ pt: 1 }}>
            {PREDEFINED_AVATARS.map((av) => (
              <Grid item key={av.key}>
                <Tooltip title="Select this avatar">
                  <IconButton
                    onClick={() => handleSelectAvatar(av.key)}
                    disabled={saving}
                    sx={{
                      p: 0.5,
                      border: selectedAvatarKey === av.key ? 3 : 1,
                      borderColor: selectedAvatarKey === av.key ? "primary.main" : "divider",
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <Avatar src={av.imageUrl} sx={{ width: 44, height: 44 }} />
                      {selectedAvatarKey === av.key && (
                        <CheckCircle
                          sx={{
                            position: "absolute",
                            bottom: -2,
                            right: -2,
                            fontSize: 18,
                            color: "primary.main",
                            bgcolor: "white",
                            borderRadius: "50%",
                          }}
                        />
                      )}
                    </Box>
                  </IconButton>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvatarOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={examBoardOpen} onClose={() => setExamBoardOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Exam Board</DialogTitle>
        <DialogContent>
          <List>
            <ListItemButton selected={examBoardId == null} onClick={() => handleExamBoardSelect(null)}>
              <ListItemText primary="All" />
              {examBoardId == null && <CheckCircle color="primary" fontSize="small" />}
            </ListItemButton>
            {examBoards.map((b) => (
              <ListItemButton
                key={b.id}
                selected={Number(examBoardId) === Number(b.id)}
                onClick={() => handleExamBoardSelect(Number(b.id))}
              >
                <ListItemText primary={b.code || b.name} secondary={b.name !== b.code ? b.name : undefined} />
                {Number(examBoardId) === Number(b.id) && <CheckCircle color="primary" fontSize="small" />}
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamBoardOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={schoolYearOpen} onClose={() => setSchoolYearOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>School Year</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="School year"
            value={draftSchoolYearId || ""}
            onChange={(e) => setDraftSchoolYearId(e.target.value ? Number(e.target.value) : null)}
            sx={{ mt: 1 }}
          >
            <MenuItem value="">Not set</MenuItem>
            {schoolYears.map((y) => (
              <MenuItem key={y.id} value={y.id}>
                {y.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchoolYearOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={handleSaveSchoolYear}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dobOpen} onClose={() => setDobOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Date of Birth</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="date"
            label="Date of birth"
            value={draftDob || ""}
            onChange={(e) => setDraftDob(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDobOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={handleSaveDob}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={targetOpen} onClose={() => setTargetOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Target Schools</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {draftTargetIds.map((id) => {
              const s = schools.find((x) => Number(x.id) === Number(id));
              return <Chip key={id} size="small" label={s?.name || id} onDelete={() => toggleTarget(id)} />;
            })}
          </Box>
          <FormGroup sx={{ maxHeight: 280, overflow: "auto" }}>
            {schools.map((s) => (
              <FormControlLabel
                key={s.id}
                control={
                  <Checkbox
                    checked={draftTargetIds.includes(Number(s.id))}
                    onChange={() => toggleTarget(Number(s.id))}
                  />
                }
                label={s.name}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTargetOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={handleSaveTargets}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={themeOpen} onClose={() => setThemeOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PaletteOutlined fontSize="small" /> Choose Theme Color
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {THEME_OPTIONS.map((t) => (
              <ListItemButton key={t.key} selected={themeKey === t.key} onClick={() => handleSaveTheme(t.key)} disabled={saving}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: t.color }} />
                </ListItemIcon>
                <ListItemText primary={t.label} />
                {themeKey === t.key && <CheckCircle color="primary" fontSize="small" />}
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThemeOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentProfile;
